import { v4 as uuid } from 'uuid';
import { getInitials, stringToHslColor } from '@commutifi-fe/utils';
import { AccountActivationStatus } from '@commutifi/models/Accounts';
import { IdentificationProtocol } from '@commutifi/constants/Authentication';
import { Account } from '@commutifi-fe/interfaces';
import { ExtendedAccount } from 'shared/interfaces/Account';
import { FormattedMessage, IntlShape } from 'locales/index';
import { EntityType } from 'constants/global';
import { isNil } from 'lodash';
import { FeatureFlagProps } from '@commutifi/models/FeatureFlag';
export const getDashboardCount = (account: ExtendedAccount | undefined, featureFlags: FeatureFlagProps | null | undefined) => {
  let count = 0;
  if (account?.managedEnterprises && account.managedEnterprises.length > 0) {
    count += account.managedEnterprises.length;
  }
  if (account && featureFlags?.userDashboard) {
    ++count;
  }
  return count;
};
export interface DashboardOptions {
  avatar: {
    text: string;
    color: string;
  };
  title: React.ReactNode;
  profile: {
    id: string;
    type: `${EntityType}`;
    name: string;
    kind?: 'enterprise' | 'organization' | 'personal';
  };
}
/**
 * @param account - Current account object with managers relation
 * @returns Home page dashboard URL base on the type of the entity
 */
export const extractDashboardOptions = (account: Pick<Account, 'managedEnterprises' | 'id' | 'name'>, featureFlags: FeatureFlagProps | null | undefined, config: {
  intl: IntlShape;
}): DashboardOptions[] => {
  const {
    intl
  } = config;
  const options: DashboardOptions[] = [];
  if (account.managedEnterprises && account.managedEnterprises.length > 0) {
    account.managedEnterprises.map(enterprise => options.push({
      ...(enterprise.kind === 'enterprise' ? {
        avatar: {
          text: intl.formatMessage({
            id: 'dashboard.enterprises.short'
          }),
          color: stringToHslColor('Enterprise')
        },
        title: intl.formatMessage({
          id: 'dashboard.enterprises'
        })
      } : {
        avatar: {
          text: intl.formatMessage({
            id: 'dashboard.organizations.short'
          }),
          color: stringToHslColor('Organization')
        },
        title: intl.formatMessage({
          id: 'dashboard.organizations'
        })
      }),
      profile: {
        id: enterprise.id || uuid(),
        type: EntityType.Admin,
        kind: enterprise.kind,
        name: enterprise.name || ''
      }
    }));
  }
  if (featureFlags?.userDashboard) {
    options.push({
      avatar: {
        text: getInitials(account.name ?? ''),
        color: stringToHslColor(account.name ?? '')
      },
      title: <FormattedMessage id="dashboard.users" />,
      profile: {
        id: account.id,
        type: EntityType.Commuter,
        name: account.name || ''
      }
    });
  }
  return options;
};
export function needsOnboardingInfo(account: Account | ExtendedAccount) {
  return isNil(account.name) || isNil(account.enterpriseId) || isNil(account.worksiteId) ||
  // account.authentication needs to be defined to check for available protocols since we don't want to block the user
  // if we have no information about if they should set their password. They can still access the dashboard with OTP or another protocol in that case.
  account.authentication && !account.authentication.availableProtocols.includes(IdentificationProtocol.Password) &&
  // SSO accounts DO NOT need to set a password. If SSO is available in protocols we don't expect the password protocol to be available.
  !account.authentication.availableProtocols.includes(IdentificationProtocol.SSO);
}
export function needsPasswordSetup(account: Account | ExtendedAccount) {
  return !account.authentication?.availableProtocols.includes(IdentificationProtocol.Password) && !account.authentication?.availableProtocols.includes(IdentificationProtocol.SSO);
}

/**
 * Callers must request `account.activationStatus`
 * (`relations: ['account']`, `fields: ['account.activationStatus']`).
 * Silently returns false when missing — guests render as non-guests.
 */
export const isGuestAccount = (account: {
  activationStatus?: AccountActivationStatus | null;
} | null | undefined): boolean => account?.activationStatus === AccountActivationStatus.Guest;